import { type FC, type CSSProperties } from 'react';
import { ATLAS_PAGES, ATLAS_SPRITE_INDEX } from '../atlasManifest';


interface AtlasBox { page: number; x: number; y: number; w: number; h: number; bbox: [number, number, number, number]; }
interface AtlasPage { w: number; h: number; }

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;

const num = (v: unknown): v is number => typeof v === 'number';

const toBox = (v: unknown): AtlasBox | null => {
  if (!isRecord(v)) return null;
  const { page, x, y, w, h, bbox } = v;
  if (!num(page) || !num(x) || !num(y) || !num(w) || !num(h) || !Array.isArray(bbox)) return null;
  const [b0, b1, b2, b3] = bbox;
  if (!num(b0) || !num(b1) || !num(b2) || !num(b3)) return null;
  return { page, x, y, w, h, bbox: [b0, b1, b2, b3] };
};

const toPage = (v: unknown): AtlasPage | null =>
  isRecord(v) && typeof v.w === 'number' && typeof v.h === 'number' ? { w: v.w, h: v.h } : null;

const root: Record<string, unknown> = isRecord(ATLAS_SPRITE_INDEX) ? ATLAS_SPRITE_INDEX : {};
const spriteMap: Record<string, unknown> = isRecord(root.sprites) ? root.sprites : {};
const pageList: unknown[] = Array.isArray(root.pages) ? root.pages : [];

const keyOf = (src: string): string => src.split('?')[0].split('/').pop() ?? src;

export const AtlasIcon: FC<{ src: string; size?: number; className?: string }> = ({ src, size = 26, className }) => {
  const box = toBox(spriteMap[keyOf(src)]);
  const page = box ? toPage(pageList[box.page]) : null;
  if (!box || !page) {
    return (
      <img src={src} alt="" draggable={false} className={className}
        style={{ width: size, height: size, objectFit: 'contain' }} />
    );
  }
  const [bx, by, bw, bh] = box.bbox;
  const scale = size / Math.max(bw, bh);
  const style: CSSProperties = {
    display: 'inline-block',
    width: bw * scale,
    height: bh * scale,
    backgroundImage: `url(${ATLAS_PAGES[box.page]})`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: `${page.w * scale}px ${page.h * scale}px`,
    backgroundPosition: `${-(box.x + bx) * scale}px ${-(box.y + by) * scale}px`,
  };
  return <span aria-hidden className={className} style={style} />;
};
